import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CalendarClock } from "lucide-react";

const empty = { av_name: "", license_info: "", license_key: "", amount: "", subscription_start: "", subscription_end: "" };

export default function AddSubscriptionDialog({ open, onOpenChange, clientId, onSaved }) {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!f.av_name && !f.license_info) { toast.error("Add a product or license"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/subscriptions`, { ...f, amount: parseFloat(f.amount) || 0, date: f.subscription_start || null });
      toast.success("Subscription added");
      setF(empty); onOpenChange(false); onSaved && onSaved(data);
    } catch (e) { toast.error("Could not add subscription"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="add-sub-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarClock size={18} className="text-primary" /> Add Subscription</DialogTitle>
          <DialogDescription>Add a new subscription or renewal for this customer.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Product / Antivirus</Label><Input value={f.av_name} onChange={(e) => set("av_name", e.target.value)} className="rounded-sm" data-testid="sub-av" /></div>
            <div className="grid gap-1.5"><Label>License</Label><Input value={f.license_info} onChange={(e) => set("license_info", e.target.value)} className="rounded-sm" data-testid="sub-license-info" /></div>
          </div>
          <div className="grid gap-1.5"><Label>License key</Label><Input value={f.license_key} onChange={(e) => set("license_key", e.target.value)} className="rounded-sm font-mono" data-testid="sub-license-key" /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="grid gap-1.5"><Label>Amount</Label><Input type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="rounded-sm font-mono" data-testid="sub-amount" /></div>
            <div className="grid gap-1.5"><Label>Start</Label><Input type="date" value={f.subscription_start} onChange={(e) => set("subscription_start", e.target.value)} className="rounded-sm" data-testid="sub-start" /></div>
            <div className="grid gap-1.5"><Label>End</Label><Input type="date" value={f.subscription_end} onChange={(e) => set("subscription_end", e.target.value)} className="rounded-sm" data-testid="sub-end" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm" onClick={submit} disabled={busy} data-testid="sub-submit">{busy ? "Adding…" : "Add Subscription"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
